import type { Translations } from "./types";

// German translations. Source of truth: data/Translation TESSA ENG-GER.xlsx
// (gitignored) — glossary supplied by the German domain expert. Where a term
// is missing, the existing AI translation is kept; see docs/translations.md.
export const de: Translations = {
  // Login
  "login.title": "Tessa",
  "login.email": "E-Mail",
  "login.password": "Passwort",
  "login.submit": "Anmelden",
  "login.submitting": "Anmeldung...",
  "login.error": "Ungültige E-Mail oder Passwort",

  // Home / Projects page
  "home.projects": "Projektverzeichnis",
  "home.searchPlaceholder": "Suche",
  "home.createNew": "Neues Projekt",
  "home.newProject": "Neues Projekt",
  "home.projectName": "Projektname",
  "home.cancel": "Abbrechen",
  "home.createProject": "Projekt erstellen",
  "home.savedDesigns": "gespeicherte Projekte",
  "home.dateCreated": "Erstellt am:",
  "home.lastModified": "Zuletzt geändert:",
  "home.noResultsTitle": "Keine Ergebnisse gefunden",
  "home.noResultsMessage":
    "Es wurde kein Ergebnis für {{search}} gefunden. Bitte versuchen Sie eine andere Suche.",
  "home.loadingProjects": "Projekte werden geladen",

  // Navigation
  "nav.settings": "Einstellungen",
  "nav.editProject": "Projekt bearbeiten",
  "nav.costSettings": "Kosteneinstellungen",
  "nav.deleteProject": "Projekt löschen",
  "nav.logout": "Abmelden",
  "nav.admin": "Verwaltung",
  "nav.canvas": "Übersicht",
  "nav.studio": "Studio",
  "nav.savedDesigns": "GESPEICHERTE PROJEKTE",
  "nav.noSavedDesigns": "Keine gespeicherten Entwürfe",
  "nav.openInCanvas": "In Übersicht öffnen",
  "nav.rename": "Umbenennen",

  // Common
  "common.cancel": "Abbrechen",
  "common.save": "Speichern",
  "common.saved": "Gespeichert",
  "common.delete": "Löschen",
  "common.deleted": "Gelöscht",
  "common.edit": "Bearbeiten",
  "common.update": "Aktualisieren",
  "common.search": "Suchen",
  "common.loading": "Laden",
  "common.total": "Gesamt",
  "common.required": "Projektname erforderlich",
  "common.continue": "Weiter",
  "common.name": "Name",

  // Delete project modal
  "deleteProject.title": "Löschen",
  "deleteProject.message":
    "Das Projekt wird dauerhaft gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.",

  // Delete design modal
  "deleteDesign.title": "{{name}} löschen?",
  "deleteDesign.message":
    "Der Entwurf wird dauerhaft gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.",

  // Update project modal
  "updateProject.title": "Projekt bearbeiten",
  "updateProject.projectName": "Projektname",
  "updateProject.notes": "Notizen",
  "updateProject.maxChars": "Maximal 120 Zeichen",
  "updateProject.placeholder": "Hier eingeben...",

  // Cost settings
  "cost.title": "Kosteneinstellungen",
  "cost.infoMessage":
    "Änderungen an den Kosteneinstellungen werden in allen Entwurfsoptionen dieses Projekts übernommen.",
  "cost.prelims": "Allgemeine Projektkosten / Baustellenausgaben",
  "cost.prelimsDesc": "angewendet auf die Baukosten",
  "cost.contingency": "Rückstellungen",
  "cost.contingencyDesc": "angewendet auf Bau- und Baustelleneinrichtungskosten",
  "cost.ohp": "Verwaltungs- und Gewinnpauschale",
  "cost.ohpDesc": "angewendet auf Bau-, Baustelleneinrichtungs- und Rückstellungskosten",

  // Inspector
  "inspector.title": "Parameterübersicht",
  "inspector.seeAllMetrics": "Alle Kennzahlen anzeigen",

  // Design metrics
  "design.title": "Entwurf",
  "design.residentialEfficiency": "Wohnraumeffizienz",
  "design.buildingEfficiency": "Gebäudeeffizienz",
  "design.floors": "Anzahl Geschosse",
  "design.buildingHeight": "Gebäudehöhe",
  "design.ffl": "FOK (Fußbodenoberkante)",
  "design.podiumHeight": "Podiumhöhe",
  "design.gia": "BGF (Bruttogeschossfläche)",
  "design.nia": "NGF (Nettogeschossfläche)",
  "design.buildingFootprint": "Aufstandsfläche",
  "design.densityUnits": "Wohndichtichte (Wohnungen / ha)",
  "design.landCoverage": "bebaute Grundstücksfläche",
  "design.northFacingUnits": "Anzahl Nach-Norden-ausgerichtete Wohneinheiten",
  "design.grossFacadeArea": "Brutto Fassadenfläche",
  "design.commercialArea": "Kommerzielle Flächen",
  "design.amenitiesArea": "Nebenflächen",
  "design.servicesArea": "Haustechnikflächen",
  "design.grz": "GRZ",
  "design.gfz": "GFZ",

  // Cost metrics
  "costMetrics.title": "Kosten",
  "costMetrics.modules": "Module",
  "costMetrics.fitOut": "Innenausbau",
  "costMetrics.baseBuild": "Nicht systemischer Baukörper",
  "costMetrics.construction": "Baukosten",
  "costMetrics.constructionPerUnit": "Baukosten / Einheit",
  "costMetrics.constructionPerArea": "Baukosten / Fläche",
  "costMetrics.perUnit": "/Einheit",
  "costMetrics.perSqm": "/m²",
  "costMetrics.perSqft": "/sqft",

  // Sustainability metrics
  "sustainability.title": "Nachhaltigkeit",
  "sustainability.indicativeFormFactor": "Indikativer Formfaktor",
  "sustainability.co2Footprint": "CO₂-Fußabdruck",
  "sustainability.co2FootprintPerArea": "CO₂-Fußabdruck / Fläche",
  "sustainability.superstructure": "Hochbau",
  "sustainability.superstructureCo2": "Hochbau - CO₂ Fußabdruck",
  "sustainability.superstructureCo2PerArea": "Hochbau - CO₂ Fußabdruck / Fläche",
  "sustainability.substructure": "Fundament und Unterbau",
  "sustainability.substructureCo2": "Fundament und Unterbau - CO₂ Fußabdruck",
  "sustainability.substructureCo2PerArea": "Fundament und Unterbau - CO₂ Fußabdruck / Fläche",
  "sustainability.steelCo2": "Stahl - CO₂ Fußabdruck",
  "sustainability.steelCo2PerArea": "Stahl - CO₂ Fußabdruck / Fläche",
  "sustainability.concreteCo2": "Beton - CO₂ Fußabdruck",
  "sustainability.concreteCo2PerArea": "Beton - CO₂ Fußabdruck / Fläche",

  // Zones
  "zones.title": "Zonen",
  "zones.type": "Nutzungsart",
  "zones.buildable": "Bebaubare Fläche",
  "zones.exclusion": "nicht zu bebauende Fläche",
  "zones.parking": "Parken",
  "zones.access": "Zufahrt",
  "zones.greenZones": "Grünflächen",
  "zones.area": "Fläche:",
  "zones.noBuildings": "Keine Gebäude in der Zone.",
  "zones.tooSmall": "Sie könnte zu klein sein.",

  // Units (measurement system)
  "units.title": "Einheiten",
  "units.total": "Gesamt:",
  "units.metric": "Metrisch",
  "units.imperial": "Imperial",
  "units.sqm": "m²",
  "units.sqft": "sqft",
  "units.m": "m",
  "units.ft": "ft",
  "units.kg": "kg",
  "units.kgSqm": "kg/m²",
  "units.kgSqft": "kg/sqft",
  "units.ha": "ha",
  "units.ac": "ac",
  "units.floors": "Geschosse",

  // Brief panel
  "brief.title": "Projektbeschreibung",
  "brief.site": "Grundstück",
  "brief.buildingSeparation": "Gebäudeabstand",
  "brief.default": "Standardwert",
  "brief.custom": "Wahleingabe",
  "brief.regulationsMayApply": "Vorschriften können gelten",
  "brief.residentialFloors": "Wohngeschosse",

  // Residential unit types
  "unitType.1b2p": "Einzimmerwohnung",
  "unitType.2b3p": "Zweizimmerwohnung (Typ 1)",
  "unitType.2b4p": "Zweizimmerwohnung (Typ 2)",
  "unitType.3b5p": "Dreizimmerwohnung (Typ 1)",
  "unitType.3b6p": "Dreizimmerwohnung (Typ 2)",

  // Custom service areas
  "serviceAreas.title": "Menü - Haustechnik",
  "serviceAreas.energyGeneration": "Elektrische Stromversorgung",
  "serviceAreas.ambientLoop": "Umgebungswärmenetz-Fernwärmesystem",
  "serviceAreas.networkDistrict": "Netz-Fernwärmesystem",
  "serviceAreas.localHeatPumps": "Lokale Wärmepumpen",
  "serviceAreas.electricalPower": "Anforderungen Stromversorgung",
  "serviceAreas.plvOnly": "Nur Primärniederspannungsraum (PNS)",
  "serviceAreas.plvSecondary": "PNS + Sekundärniederspannungsraum",
  "serviceAreas.plvGenerator": "PNS + Notstromaggregat",
  "serviceAreas.extras": "Extras",
  "serviceAreas.sprinklers": "Sprinkler",
  "serviceAreas.electricalSubstation": "Elektrische Umspannstation",
  "serviceAreas.rainwaterHarvesting": "Regenwassernutzungsanlage",
  "serviceAreas.greywaterHarvesting": "Abwasseraufbereitungsanlage",

  // Podium
  "podium.title": "Podium",
  "podium.use": "Nutzung",
  "podium.commercial": "Kommerzielle Nutzung",
  "podium.levels": "Geschosse",
  "podium.height": "Höhe",

  // Facade
  "facade.title": "Fassadenmaterial",
  "facade.traditionalBrickwork": "Klinker",
  "facade.precastConcrete": "Vorgefertigte Betonfassadenelemente",
  "facade.fibreCement": "Faserzement Fassadensystem",
  "facade.mechanicalBrickSlip": "Mechanisches Ziegelslipsystem",
  "facade.metalCladding": "Metallfassade",

  // Metrics drawer
  "metrics.title": "Kennzahlen",
  "metrics.areaMeasurements": "Flächen",
  "metrics.level": "Geschoss",
  "metrics.gea": "Bruttoaußenfläche",
  "metrics.efficiency": "Effizienz",
  "metrics.unitMix": "Wohnungsmix",
  "metrics.type": "Typ",
  "metrics.avgArea": "Ø Fläche",
  "metrics.quantity": "Anzahl",
  "metrics.mix": "Anteil",
  "metrics.nonResidentialAreas": "Nutzflächen",
  "metrics.area": "Fläche",
  "metrics.footprint": "Grundfläche",
  "metrics.commercials": "Kommerzielle Nutzung - ",
  "metrics.services": "Haustechnikräume - ",
  "metrics.ground": "Erdgeschoss",
  "metrics.amenities": "Nebenräume - ",
  "metrics.nonResidentialNote":
    "Gesamte Nutzfläche geteilt durch die gesamte Bruttoaußenfläche aller Geschosse.",
  "metrics.showMeasurementsIn": "Maßeinheit",
  "metrics.blocksUnits": "Wohneinheiten",
  "metrics.blocksModules": "Module",

  // Floor labels
  "floor.ground": "Erdgeschoss",
  "floor.1st": "1. OG",
  "floor.2nd": "2. OG",
  "floor.3rd": "3. OG",
  "floor.nth": "{{n}}. OG",
  "floor.roof": "Dach",
  "floor.short.ground": "EG",
  "floor.short.1st": "1. OG",
  "floor.short.2nd": "2. OG",
  "floor.short.3rd": "3. OG",
  "floor.short.nth": "{{n}}. OG",

  // Block labels
  "block.label": "Gebäude {{letter}}",

  // Cost summary
  "costSummary.title": "Zusammenfassung",
  "costSummary.cost": "Kosten",
  "costSummary.perUnit": "Pro Einheit",
  "costSummary.per": "Pro ",
  "costSummary.modular": "Modular",
  "costSummary.fitOut": "Innenausbau",
  "costSummary.baseBuild": "Nicht systemischer Baukörper",
  "costSummary.construction": "Baukosten",
  "costSummary.prelims": "Allgemeine Projektkosten / Baustellenausgaben",
  "costSummary.contingency": "Rückstellungen",
  "costSummary.ohp": "Verwaltungs- und Gewinnpauschale",
  "costSummary.prelimsTooltip":
    "Standardwert ist 10 %, angewendet auf die Baukosten. Gehen Sie zu den Kosteneinstellungen, um dies anzupassen.",
  "costSummary.contingencyTooltip":
    "Standardwert ist 3 %, angewendet auf Bau- und Baustelleneinrichtungskosten. Gehen Sie zu den Kosteneinstellungen, um dies anzupassen.",
  "costSummary.ohpTooltip":
    "Standardwert ist 5,5 %, angewendet auf Bau-, Baustelleneinrichtungs- und Rückstellungskosten. Gehen Sie zu den Kosteneinstellungen, um dies anzupassen.",
  "costSummary.totalTooltip":
    "Gesamtkosten sind die Summe aller Kosten. Gehen Sie zu den Kosteneinstellungen, um dies anzupassen.",
  "costSummary.moduleElement": "Systemischer Baukörper",

  // Sustainability summary
  "sustainSummary.title": "Zusammenfassung",
  "sustainSummary.footprint": "Fußabdruck",
  "sustainSummary.footprintPer": "Fußabdruck pro ",
  "sustainSummary.substructure": "Fundament und Unterbau",
  "sustainSummary.superstructure": "Hochbau",
  "sustainSummary.other":
    "Sonstiges (Innenausbau & Möbel; Ausstattung, Einrichtung & Geräte; Haustechnik.)",
  "sustainSummary.carbonNote":
    "Wir arbeiten an der genauen Messung des CO₂-Fußabdrucks des Unterbaus, der in der nächsten Version von TESSA enthalten sein wird",

  // Explore panel
  "explore.designOptions": "Entwurfsoptionen",
  "explore.of": "von",
  "explore.columns": "Filter",
  "explore.chooseColumns": "Filterelemente",
  "explore.resetToDefault": "Zurück zu Standardeinstellungen",
  "explore.selected": "Ausgewählt",
  "explore.noDesignOptions": "Keine Entwurfsoptionen verfügbar",
  "explore.basedOnInputs": "basierend auf Zonen- und Entwurfsvorgaben",
  "explore.pin": "Anheften",
  "explore.design": "Entwurf",
  "explore.quantity": "Menge",
  "explore.measurement": "Messung",
  "explore.calculated": "Berechnet",
  "explore.constructionCost": "Baukosten",
  "explore.totalCost": "Gesamtkosten",
  "explore.outputs": "Kosten pro Einheit",
  "explore.embodiedCarbonPerArea": "Graue Energie pro Fläche",
  "explore.totalEmbodiedCarbon": "Graue Energie gesamt",
  "explore.operationalCarbon": "Betriebsbedingtes CO₂",

  // Explore table columns
  "column.name": "Name",
  "column.image": "Bild",
  "column.units": "Anzahl Wohneinheiten",
  "column.buildings": "Gebäudeanzahl",
  "column.modules": "Anzahl Module",
  "column.floors": "Anzahl Geschosse",
  "column.floorsResidential": "Anzahl Wohngeschosse",
  "column.podiumLevels": "Anzahl Podiumgeschosse",
  "column.cores": "Anzahl Treppenhäuser",
  "column.liftCores": "Anzahl Aufzugsschächte",
  "column.firefightingCores": "Anzahl Sicherheitstreppenräume",
  "column.entrances": "Anzahl Eingänge",
  "column.unitsPerCore": "Wohnungen pro Treppenraum",
  "column.dualAspectUnits": "Wohnung mit zweiseitiger Ausrichtung",
  "column.partMCompliantUnits": "Behindertengerechte Einheiten",
  "column.density": "Wohndichtichte (Wohnungen / ha)",
  "column.densityHabitableRooms": "Wohnraumdichte (Räume / ha)",
  "column.wallToFloorRatio": "Wand / Boden Verhältnis",
  "column.coreAndCorridorArea": "Allgemeinflächen",
  "column.nonResidentialArea": "Nutzflächen",
  "column.serviceArea": "Haustechnikflächen",
  "column.roofArea": "Dachfläche",
  "column.glazedFacadeArea": "Verglaste Fassadenfläche",
  "column.netFacadeArea": "Netto Fassadefläche",
  "column.concaveFacadeCorner": "Geradenmaß Konkave Fassadenecke",
  "column.convexFacadeCorner": "Geradenmaß Konvexe Fassadenecke",

  // Editor
  "editor.3d": "3D",
  "editor.2d": "2D",
  "editor.labels": "Beschriftungen",
  "editor.2dBlockView": "2D-Blockansicht",
  "editor.allZones": "Alle Zonen",
  "editor.allBlocks": "Alle Blöcke",
  "editor.legend": "Legende",
  "editor.residentialAreas": "WOHNFLÄCHEN",
  "editor.nonResidentialAreas": "NICHT-WOHNFLÄCHEN",
  "editor.compass": "N",

  // Canvas toolbox
  "toolbox.back": "Zurück",
  "toolbox.forward": "Vor",
  "toolbox.undo": "Rückgängig",
  "toolbox.redo": "Wiederherstellen",
  "toolbox.create": "Grundstücksmarkierung",
  "toolbox.cut": "Grundstücksteilung",
  "toolbox.merge": "Verbinden",
  "toolbox.delete": "Löschen",
  "toolbox.edgeOffset": "Abstandsflächen",
  "toolbox.save": "Speichern",
  "toolbox.export": "Exportieren",
  "toolbox.exportBrief": "Projektbeschreibung-Export",
  "toolbox.exportFull": "Vollständiger Export",
  "toolbox.summaryPdf": "Zusammenfassung (PDF)",
  "toolbox.select": "Anzeige / Auswählen",
  "toolbox.search": "Adresssuche",

  // Map view
  "mapView.title": "Kartenansicht",
  "mapView.map": "Karteneigenschaften",
  "mapView.abstract": "Abstrakt",
  "mapView.architectural": "Render",
  "mapView.custom": "Wahleingabe",
  "mapView.light": "Schematische Karte",
  "mapView.satellite": "Satellitenkarte",
  "mapView.street": "Straßenkarte",
  "mapView.siteContext": "Projektumgebung",
  "mapView.topography": "Topografie",
  "mapView.zones": "Zonen",

  // Stencil overlay
  "stencil.title": "Schablonen-Overlay",
  "stencil.upload": "PNG hochladen",
  "stencil.uploadFile": "PNG oder WMS-XML hochladen",
  "stencil.opacity": "Deckkraft",
  "stencil.remove": "Entfernen",
  "stencil.lock": "Auf Karte fixieren",
  "stencil.unlock": "Lösen",
  "stencil.wmsLayer": "WMS-Ebene",
  "stencil.error.invalidWms": "Nicht unterstütztes WMS-Dateiformat",
  "stencil.error.unsupportedFile": "Nicht unterstützter Dateityp",
  "stencil.error.tileLoad":
    "WMS-Kacheln konnten nicht geladen werden. Der Server unterstützt evtl. kein CORS oder EPSG:3857.",
  "stencil.error.fetchFailed":
    "URL konnte nicht geladen werden. CORS evtl. nicht erlaubt oder URL falsch.",
  "stencil.placementTitle": "Schablone positionieren",
  "stencil.cancel": "Abbrechen",
  "stencil.size": "Größe",
  "stencil.editMode": "Bearbeitungsmodus",
  "stencil.done": "Fertig",
  "stencil.catalogDefaults": "Standard-Layer",
  "stencil.catalogSaved": "In diesem Projekt gespeichert",
  "stencil.searchPlaceholder": "Layer suchen…",
  "stencil.noResults": "Keine Layer gefunden",
  "stencil.active": "Aktiv",
  "stencil.cat.land-registry": "Liegenschaft",
  "stencil.cat.planning-law": "Bauleitplanung",
  "stencil.cat.brw": "Bodenrichtwerte (BRW)",
  "stencil.addCustom": "Eigenen Layer hinzufügen",
  "stencil.activeOverlay": "Aktives Overlay",
  "stencil.tabFile": "Datei",
  "stencil.tabUrl": "URL",
  "stencil.urlPlaceholder": "https://example.de/wms",
  "stencil.urlLoad": "Laden",
  "stencil.removeSaved": "Aus Gespeicherten entfernen",

  // Building Presets
  "presets.title": "Gebäudekomponenten",
  "presets.unit": "Wohneinheit",
  "presets.cores": "Treppenhäuser",
  "presets.corridors": "Korridore",
  "presets.services": "Haustechnikräume",
  "presets.amenities": "Nebenräume",
  "presets.entrances": "Eingänge",
  "presets.facade": "Fassade",
  "presets.roof": "Dächer",
  "presets.parapets": "Brüstungen",
  "presets.floors": "Geschossdecken",
  "presets.ceilings": "Decken",
  "presets.walls": "Wände",
  "presets.windows": "Fenster",
  "presets.doors": "Türen",
  "presets.structure": "Tragkonstruktion",
  "presets.commercials": "Kommerzielle Nutzung",
  "presets.floorLevels": "Geschosse",

  // Plan2D room types
  "roomType.corridor": "Flur",
  "roomType.core": "Treppenhaus",
  "roomType.entrance": "Eingang",
  "roomType.amenity": "Nebenraum",
  "roomType.service": "Haustechnikraum",
  "roomType.commercialSpace": "Kommerzielle Nutzung",

  // Currency settings
  "currency.title": "Währungssymbol",
  "currency.description":
    "Überschreiben Sie das angezeigte Währungssymbol in der App. Die zugrunde liegenden Kostendaten bleiben unverändert — dies dient nur zu Demonstrationszwecken.",
  "currency.label": "Währung",
  "currency.eur": "EUR — €",
  "currency.gbp": "GBP — £",
  "currency.storedNote":
    "Gespeichert im localStorage. Änderungen werden sofort in allen geöffneten Tabs wirksam.",

  // Language
  "language.title": "Sprache",
  "language.en": "English",
  "language.de": "Deutsch",

  // Save Design modal
  "saveModal.title": "Entwurf speichern",

  // Export modal
  "export.title": "Exportieren",
  "export.saveAndExport": "Speichern & Exportieren",
  "export.bimPackage": "BIM Revit 2021 Paket",
  "export.metricsLabel": "Kennzahlen",
  "export.mustSaveNotice": "Sie müssen diesen Entwurf speichern, um zu exportieren",
  "export.creditsRequired": "Der Export dieser Datei erfordert {{count}} Credits.",
  "export.creditsCurrent": "Sie haben derzeit {{count}} Credits.",
  "export.creditUnit": "1 Einheit = 1 Credit.",
  "export.emailNotice":
    "Wir senden eine E-Mail an {{email}} mit einem Link zum Herunterladen Ihrer Dateien, sobald diese bereit sind.",
  "export.allZones": "Alle Zonen",
  "export.allBuildings": "Alle Gebäude",
  "export.areaSection": "Bereich",
  "export.zoneLabel": "Zone",
  "export.buildingLabel": "Gebäude",
  "export.designOption": "Entwurfsoption #{{n}}",
  "export.blocksSelected": "Blöcke ausgewählt",

  // Override canvas design modal
  "overrideCanvas.title": "In Übersicht öffnen",
  "overrideCanvas.message":
    "Dies ersetzt die Zonen und die Projektbeschreibung in der Übersicht durch die aus {{name}}. Möchten Sie fortfahren?",
};
